import { Injectable, inject } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import {
  BENCHMARK_MAX_TRANSACTIONS,
  RANGED_BUILDING_BAND,
  isLandLed,
  isSupplyOverrideComplete,
  needsBuildingRate,
  roadAdjustmentFor,
} from '../config/ci-options.config';

import { stepsFor } from '../config/ci-steps.config';
import {
  BenchmarkForm,
  BenchmarkingForm,
  BuildingValueForm,
  CiForm,
  LandValueForm,
  PropertyCategory,
  PropertyForm,
  StepId,
  TransactionForm,
  TransactionValue,
  UdsRentalForm,
} from '../models/ci-form.model';
import { CiQuestion } from '../models/ci-question.model';

export function positiveNumber(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === '') return null;
  return Number(value) > 0 ? null : { positiveNumber: true };
}

@Injectable({ providedIn: 'root' })
export class CiFormService {
  private readonly fb = inject(NonNullableFormBuilder);

  createForm(
    questions: CiQuestion[],
    category: PropertyCategory = 'primary-project',
  ): CiForm {
    const form = this.fb.group({
      category: this.fb.control<PropertyCategory>(category, Validators.required),
      property: this.propertyGroup(),
      benchmarking: this.benchmarkingGroup(),
      udsRental: this.udsRentalGroup(),
      landValue: this.landValueGroup(),
      buildingValue: this.buildingValueGroup(),
      benchmark: this.benchmarkGroup(questions),
    }) as CiForm;

    this.applyCategoryRules(form, category);
    return form;
  }

  private propertyGroup(): PropertyForm {
    return this.fb.group({
      ciReference: this.fb.control(this.nextCiReference()),
      propertyId: this.fb.control(''),
      propertyName: this.fb.control('', Validators.required),
      microMarket: this.fb.control('', Validators.required),
      cluster: this.fb.control(''),
      size: this.fb.control<number | null>(null, [Validators.required, positiveNumber]),
      configuration: this.fb.control(''),
      listingUnitName: this.fb.control(''),
      quotedPrice: this.fb.control<number | null>(null, [
        Validators.required,
        positiveNumber,
      ]),
    }) as PropertyForm;
  }

  /* ── Benchmarking (step 2) ────────────────────────────────────────────── */

  private benchmarkingGroup(): BenchmarkingForm {
    const transactions = this.fb.array(
      [this.createTransactionRow()],
    );

    return this.fb.group({
      source: this.fb.control<BenchmarkingForm['value']['source']>('transactions'),
      transactions,
      latestPsfOverride: this.fb.control<number | null>(null, positiveNumber),
      latestDateOverride: this.fb.control(''),
      similarProperties: this.fb.array<
        FormGroup<{ name: FormControl<string>; psf: FormControl<number | null> }>
      >([]),
    }) as BenchmarkingForm;
  }

  createTransactionRow(): TransactionForm {
    return this.fb.group({
      psf: this.fb.control<number | null>(null, positiveNumber),
      source: this.fb.control(''),
      date: this.fb.control(''),
    }) as TransactionForm;
  }

  addTransaction(form: BenchmarkingForm): void {
    if (form.controls.transactions.length >= BENCHMARK_MAX_TRANSACTIONS) return;
    form.controls.transactions.push(this.createTransactionRow());
    form.controls.transactions.markAsDirty();
  }

  removeTransaction(form: BenchmarkingForm, index: number): void {
    const rows = form.controls.transactions;
    if (rows.length <= 1 || index < 0 || index >= rows.length) return;
    rows.removeAt(index);
    rows.markAsDirty();
  }

  setTransactions(form: BenchmarkingForm, values: TransactionValue[]): void {
    const rows = form.controls.transactions;
    rows.clear({ emitEvent: false });
    for (const value of values.slice(0, BENCHMARK_MAX_TRANSACTIONS)) {
      const row = this.createTransactionRow();
      row.patchValue(value);
      rows.push(row, { emitEvent: false });
    }
    if (!rows.length) rows.push(this.createTransactionRow(), { emitEvent: false });
    rows.updateValueAndValidity();
  }

  addSimilarProperty(form: CiForm): void {
    form.controls.benchmarking.controls.similarProperties.push(
      this.fb.group({
        name: this.fb.control(''),
        psf: this.fb.control<number | null>(null, positiveNumber),
      }),
    );
  }

  removeSimilarProperty(form: CiForm, index: number): void {
    form.controls.benchmarking.controls.similarProperties.removeAt(index);
  }

  /* ── UDS, rental & trophy override (step 3) ──────────────────────────── */

  private udsRentalGroup(): UdsRentalForm {
    return this.fb.group({
      udsSqft: this.fb.control<number | null>(null, positiveNumber),
      currentRatePsf: this.fb.control<number | null>(null, positiveNumber),
      monthlyRent: this.fb.control<number | null>(null, positiveNumber),
      overrideRentalYield: this.fb.control(false),
      manualRentalYieldPct: this.fb.control<number | null>(null, positiveNumber),
      trophyOverride: this.fb.control(false),
    }) as UdsRentalForm;
  }

  private landValueGroup(): LandValueForm {
    const group = this.fb.group({
      landRatePsf: this.fb.control<number | null>(null, positiveNumber),
      roadWidthFt: this.fb.control<number | null>(null, positiveNumber),
      cornerPlot: this.fb.control(false),
      frontageFt: this.fb.control<number | null>(null, positiveNumber),
      zoning: this.fb.control(''),
      orientation: this.fb.control('east'),
      roadAdjustment: this.fb.control(0.15),
      premiumAdjustment: this.fb.control(0.1),
      supplyOverride: this.fb.control(false),
      supplyOverrideValue: this.fb.control<number | null>(null, positiveNumber),
      supplyOverrideReason: this.fb.control(''),
    }) as LandValueForm;
    // A typed road width decides the band; the backend rejects a dropdown that disagrees.
    group.controls.roadWidthFt.valueChanges.subscribe((width) => {
      const ft = Number(width);
      if (width != null && `${width}` !== '' && Number.isFinite(ft) && ft > 0) {
        group.controls.roadAdjustment.setValue(roadAdjustmentFor(ft));
      }
    });
    return group;
  }

  private buildingValueGroup(): BuildingValueForm {
    return this.fb.group({
      builtUpArea: this.fb.control<number | null>(null, positiveNumber),
      ageYears: this.fb.control<number | null>(null, Validators.min(0)),
      constructionClass: this.fb.control(''),
      buildingRatePsf: this.fb.control<number | null>(null, positiveNumber),
      floors: this.fb.control<number | null>(null),
    }) as BuildingValueForm;
  }

  private benchmarkGroup(questions: CiQuestion[]): BenchmarkForm {
    const answers: Record<string, FormControl<string>> = {};
    for (const q of questions) {
      answers[q.key] = this.fb.control(''); // Optional: unanswered factors carry no weight.
    }
    return this.fb.group({
      // Backend derives this for the transactions source; a value is only
      // demanded when the analyst sets it manually (see isStepValid).
      baseBenchmarkPsf: this.fb.control<number | null>(null, positiveNumber),
      overrideBase: this.fb.control(false),
      answers: this.fb.group(answers),
    }) as BenchmarkForm;
  }

  /**
   * Required fields follow the flow. A plot never asks for a configuration; a
   * house must declare its land rate and built-up area.
   */
  applyCategoryRules(form: CiForm, category: PropertyCategory): void {
    const p = form.controls.property;
    const land = form.controls.landValue;
    const building = form.controls.buildingValue;
    const landLed = isLandLed(category);

    this.setRequired(p.controls.configuration, category === 'primary-project');
    this.setRequired(p.controls.listingUnitName, category === 'resale');

    const house = category === 'independent-house';
    this.setRequired(land.controls.landRatePsf, landLed);
    this.setRequired(land.controls.orientation, landLed);
    this.setRequired(building.controls.builtUpArea, house);
    this.setRequired(building.controls.ageYears, house);
    this.setRequired(building.controls.constructionClass, house);

    form.updateValueAndValidity({ emitEvent: false });
  }

  private setRequired(control: FormControl<unknown>, required: boolean): void {
    if (required) control.addValidators(Validators.required);
    else control.removeValidators(Validators.required);
    control.updateValueAndValidity({ emitEvent: false });
  }

  controlsForStep(form: CiForm, step: StepId): AbstractControl | null {
    switch (step) {
      case 'property':
        return form.controls.property;
      case 'transactions':
        return form.controls.benchmarking;
      case 'uds-rental':
        return form.controls.udsRental;
      case 'land-value':
        return form.controls.landValue;
      case 'building-value':
        return form.controls.buildingValue;
      case 'benchmark':
        return form.controls.benchmark;
      case 'adjustments':
        return form.controls.landValue;
      case 'review':
        return null;
    }
  }

  isStepValid(form: CiForm, step: StepId): boolean {
    if (step === 'transactions') {
      return this.hasUsableBenchmarkInput(form) && form.controls.benchmarking.valid;
    }
    const control = this.controlsForStep(form, step);
    const land = form.controls.landValue.controls;
    switch (step) {
      case 'benchmark': {
        const benchmark = form.controls.benchmark;
        return benchmark.valid && this.hasUsableBenchmarkInput(form);
      }
      // Land Value and Adjustments share one form group, so each checks its own fields.
      case 'land-value':
        return [land.landRatePsf, land.roadWidthFt, land.frontageFt, land.zoning]
          .every((c) => c.valid);
      case 'adjustments':
        return [land.orientation, land.roadAdjustment, land.premiumAdjustment].every((c) => c.valid)
          && isSupplyOverrideComplete(form.controls.landValue.getRawValue());
      case 'building-value':
        return form.controls.buildingValue.valid
          && this.isBuildingRateComplete(form.controls.buildingValue);
    }
    return control ? control.valid : this.isFlowValid(form);
  }

  /** The 13–15 year band needs a rate chosen inside its permitted range. */
  isBuildingRateComplete(building: BuildingValueForm): boolean {
    const { ageYears, constructionClass, buildingRatePsf } = building.getRawValue();
    if (ageYears == null || !needsBuildingRate(Number(ageYears))) return true;
    const range = RANGED_BUILDING_BAND.rates[constructionClass as 'basic' | 'premium'];
    if (!range) return true;
    const rate = Number(buildingRatePsf);
    return buildingRatePsf != null && rate >= range.min && rate <= range.max;
  }

  /** Only the steps in the active flow have to be valid. */
  isFlowValid(form: CiForm): boolean {
    const category = form.controls.category.value;
    return stepsFor(category)
      .filter((s) => s.id !== 'review')
      .every((s) => this.isStepValid(form, s.id));
  }

  /** Every non-land CI benchmark needs one dated PSF record. Extra rows remain
   *  optional, so a comparable with only one CI transaction is valid. */
  hasUsableBenchmarkInput(form: CiForm): boolean {
    const benchmarking = form.controls.benchmarking.getRawValue();
    if (isLandLed(form.controls.category.value)) {
      const base = form.controls.benchmark.controls.baseBenchmarkPsf.value;
      return !!base && base > 0;
    }
    return benchmarking.transactions.some((t) => !!t.psf && !!t.date);
  }

  markStepTouched(form: CiForm, step: StepId): void {
    const control = this.controlsForStep(form, step);
    this.markAllTouched(control ?? form);
  }

  markFlowTouched(form: CiForm): void {
    const category = form.controls.category.value;
    for (const step of stepsFor(category)) {
      const control = this.controlsForStep(form, step.id);
      if (control) this.markAllTouched(control);
    }
  }

  private markAllTouched(control: AbstractControl): void {
    control.markAsTouched();
    control.updateValueAndValidity({ emitEvent: false });
    if (control instanceof FormGroup || control instanceof FormArray) {
      Object.values(control.controls).forEach((c) =>
        this.markAllTouched(c as AbstractControl),
      );
    }
  }

  private nextCiReference(): string {
    return `CI-${new Date().getFullYear()}-0001`;
  }
}
